import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { TableSkeleton } from '../../lib/skeletons';
import { toast } from 'sonner';
import {
  Package, Search, RefreshCw, Truck, FileText, Receipt,
  ChevronLeft, ChevronRight, AlertTriangle, CheckCircle2,
  Loader2, Eye, Lock, Building2, Calendar, ClipboardCheck,
} from 'lucide-react';

const fmtCLP = (n) => (Number(n) || 0).toLocaleString('es-CL');

const fmtFechaCorta = (s) => {
  if (!s) return '—';
  // ISO YYYY-MM-DD... → DD-MM-YYYY (Iter 292.g: 4-digit year para consistencia global)
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (isoMatch) return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  // Access "MM/DD/YY 0"
  const accessMatch = /^(\d{2})\/(\d{2})\/(\d{2})/.exec(s);
  if (accessMatch) return `${accessMatch[2]}-${accessMatch[1]}-20${accessMatch[3]}`;
  return s;
};

const TIPO_DTE_LABEL = {
  '33': 'Factura', '34': 'F.Exenta', '39': 'Boleta',
  '41': 'B.Exenta', '52': 'G.Despacho', '56': 'N.Débito', '61': 'N.Crédito',
};

/**
 * Iter 236.m.16 (Carlos) — Listado de OCs pendientes de recepción en Bodega.
 * Muestra OCs aprobadas con `requiere_recepcion=true`, sin recepción todavía,
 * y permite a Bodega ir directamente a recepcionarlas. Si la OC ya tiene una
 * factura asociada (DTE), aparece destacada como prioridad alta.
 */
export default function OCPendientesRecepcionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = (user?.rol || user?.role || '').toLowerCase();
  const canCerrarManual = ['admin', 'administrador', 'gerencia', 'bodega_admin', 'jefe_bodega'].includes(role);

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [soloConFactura, setSoloConFactura] = useState(false);
  const [incluirRecibidas, setIncluirRecibidas] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Dialog cierre manual
  const [cerrarDlg, setCerrarDlg] = useState(null);
  const [cerrarMotivo, setCerrarMotivo] = useState('');
  const [cerrarLoading, setCerrarLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * pageSize);
      params.append('limit', pageSize);
      if (q) params.append('q', q);
      if (soloConFactura) params.append('solo_con_factura', 'true');
      if (incluirRecibidas) params.append('incluir_recibidas', 'true');
      const res = await api.get(`/bodega/oc-pendientes-recepcion?${params}`);
      setItems(res.data.items || []);
      setStats({ total: res.data.total || 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando OCs pendientes');
    } finally {
      setLoading(false);
    }
  }, [q, soloConFactura, incluirRecibidas, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRecepcionar = (numeroOc) => {
    navigate(`/bodega/recepcion-oc?oc=${encodeURIComponent(numeroOc)}`);
  };

  const handleVerFactura = async (folio, rutEmisor) => {
    // Abrir DTE en visor (mismo endpoint que AR usa)
    try {
      const origin = window.location.origin;
      const url = `${origin}/api/dte/${folio}/pdf?rut_emisor=${encodeURIComponent(rutEmisor)}`;
      window.open(url, '_blank', 'noopener');
    } catch {
      toast.error('No se pudo abrir el PDF de la factura');
    }
  };

  const abrirCerrar = (item) => {
    setCerrarDlg(item);
    setCerrarMotivo('');
  };

  const confirmarCerrar = async () => {
    if ((cerrarMotivo || '').trim().length < 5) {
      toast.error('El motivo debe tener al menos 5 caracteres');
      return;
    }
    setCerrarLoading(true);
    try {
      await api.patch(`/compras/ordenes-compra/${cerrarDlg.numero_oc}/cerrar-recepcion`, {
        motivo: cerrarMotivo.trim(),
      });
      toast.success(`OC ${cerrarDlg.numero_oc} marcada como recepción cerrada`);
      setCerrarDlg(null);
      setCerrarMotivo('');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cerrando recepción');
    } finally {
      setCerrarLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(stats.total / pageSize));

  return (
    <div className="p-4 space-y-4" data-testid="oc-pendientes-recepcion-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Package className="w-5 h-5 text-amber-600" />
            OC Pendientes de Recepción
          </h1>
          <p className="text-xs text-muted-foreground">
            Listado de Órdenes de Compra aprobadas que requieren recepción de bodega.
            Marca <b>"Cerrar manual"</b> si la mercadería ya está pero no necesitás registrar recepción formal.
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" data-testid="btn-refresh-pendientes">
          <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Card data-testid="stat-total">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase text-muted-foreground font-semibold">Pendientes</p>
            <p className="text-2xl font-bold text-amber-700">{stats.total}</p>
          </CardContent>
        </Card>
        <Card data-testid="stat-con-factura">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase text-muted-foreground font-semibold">Con factura DTE</p>
            <p className="text-2xl font-bold text-emerald-700">
              {items.filter(i => i.tiene_factura).length}
              <span className="text-[10px] text-muted-foreground ml-1">/ página</span>
            </p>
          </CardContent>
        </Card>
        <Card data-testid="stat-sin-factura" className="hidden sm:block">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase text-muted-foreground font-semibold">Sin factura aún</p>
            <p className="text-2xl font-bold text-blue-700">
              {items.filter(i => !i.tiene_factura).length}
              <span className="text-[10px] text-muted-foreground ml-1">/ página</span>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por N° OC, proveedor o OT…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            className="pl-9 h-9 text-sm"
            data-testid="input-buscar-pendientes"
          />
        </div>
        <Button
          type="button"
          size="sm"
          variant={soloConFactura ? 'default' : 'outline'}
          onClick={() => { setSoloConFactura(!soloConFactura); setPage(0); }}
          data-testid="chip-solo-con-factura"
          className={soloConFactura ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-emerald-700 border-emerald-300'}
        >
          <Receipt className="w-3.5 h-3.5 mr-1" />
          {soloConFactura ? '✓ ' : ''}Con factura
        </Button>
        <Button
          type="button"
          size="sm"
          variant={incluirRecibidas ? 'default' : 'outline'}
          onClick={() => { setIncluirRecibidas(!incluirRecibidas); setPage(0); }}
          data-testid="chip-incluir-recibidas"
          className={incluirRecibidas ? 'bg-slate-600 hover:bg-slate-700' : 'text-slate-700 border-slate-300'}
        >
          <ClipboardCheck className="w-3.5 h-3.5 mr-1" />
          {incluirRecibidas ? '✓ ' : ''}Incluir parcialmente recibidas
        </Button>
      </div>

      {/* Lista */}
      {loading ? (
        <TableSkeleton rows={6} cols={5} />
      ) : items.length === 0 ? (
        <Card data-testid="pendientes-empty">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm font-semibold">No hay OCs pendientes de recepción</p>
            <p className="text-xs text-muted-foreground mt-1">
              Todas las OCs marcadas con "solicitar recepción" ya tienen su recepción registrada o están cerradas.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="pendientes-list">
          {items.map((it) => (
            <Card
              key={it.numero_oc}
              className={`hover:shadow-md transition-shadow ${
                it.tiene_factura ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-amber-400'
              }`}
              data-testid={`pendiente-${it.numero_oc}`}
            >
              <CardContent className="p-3">
                <div className="flex flex-col lg:flex-row lg:items-start gap-3">
                  {/* Info principal */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-mono font-bold text-base text-blue-700" data-testid={`oc-num-${it.numero_oc}`}>
                        OC {it.numero_oc}
                      </span>
                      {it.tiene_factura ? (
                        <Badge variant="default" className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1">
                          <Receipt className="w-3 h-3" /> {it.facturas.length} DTE
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-700 border-amber-300">
                          <Calendar className="w-3 h-3 mr-1" /> Sin factura aún
                        </Badge>
                      )}
                      {it.tiene_recepcion && (
                        <Badge variant="outline" className="text-blue-700 border-blue-300">
                          <ClipboardCheck className="w-3 h-3 mr-1" /> Recepción parcial
                        </Badge>
                      )}
                      {it.ot_numero && (
                        <Badge variant="outline" className="text-slate-700">OT {it.ot_numero}</Badge>
                      )}
                      <span className="text-[11px] text-muted-foreground">
                        {fmtFechaCorta(it.fecha_creacion || it.fecha)}
                      </span>
                    </div>

                    <div className="flex items-start gap-2 text-sm">
                      <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="font-semibold truncate" title={it.nombre_proveedor}>
                          {it.nombre_proveedor || it.rut_proveedor || '—'}
                        </p>
                        <p className="text-[11px] text-muted-foreground font-mono">{it.rut_proveedor}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-xs mt-1.5 text-muted-foreground">
                      <span>Neto: <b className="text-foreground font-mono">${fmtCLP(it.neto)}</b></span>
                      <span>Total: <b className="text-foreground font-mono">${fmtCLP(it.total)}</b></span>
                      {it.solicitante && <span className="truncate">Solic: {it.solicitante}</span>}
                    </div>

                    {/* Facturas asociadas */}
                    {it.facturas.length > 0 && (
                      <div className="mt-2 pl-6 space-y-1" data-testid={`facturas-${it.numero_oc}`}>
                        {it.facturas.slice(0, 3).map(f => (
                          <button
                            key={f.folio}
                            type="button"
                            onClick={() => handleVerFactura(f.folio, f.rut_emisor)}
                            className="w-full sm:w-auto flex items-center gap-2 text-[11px] bg-emerald-50 hover:bg-emerald-100 text-emerald-800 px-2 py-1 rounded border border-emerald-200 transition-colors"
                            data-testid={`ver-factura-${it.numero_oc}-${f.folio}`}
                            title="Ver PDF de la factura"
                          >
                            <FileText className="w-3 h-3" />
                            <span className="font-mono font-semibold">{TIPO_DTE_LABEL[f.tipo_dte] || `T${f.tipo_dte}`} {f.folio}</span>
                            <span className="text-emerald-700">${fmtCLP(f.monto)}</span>
                            <span className="text-emerald-600/70">{fmtFechaCorta(f.fecha)}</span>
                          </button>
                        ))}
                        {it.facturas.length > 3 && (
                          <span className="text-[10px] text-muted-foreground">
                            + {it.facturas.length - 3} factura(s) más
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Acciones */}
                  <div className="flex flex-row lg:flex-col gap-2 lg:w-[200px] flex-shrink-0">
                    <Button
                      onClick={() => handleRecepcionar(it.numero_oc)}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5 flex-1 lg:w-full"
                      data-testid={`btn-recepcionar-${it.numero_oc}`}
                    >
                      <Truck className="w-4 h-4" /> Recepcionar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/compras/ordenes-compra?open=${encodeURIComponent(it.numero_oc)}`)}
                      className="gap-1.5 flex-1 lg:w-full"
                      data-testid={`btn-ver-oc-${it.numero_oc}`}
                    >
                      <Eye className="w-4 h-4" /> Ver OC
                    </Button>
                    {canCerrarManual && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => abrirCerrar(it)}
                        className="gap-1.5 flex-1 lg:w-full text-slate-600 border-slate-300 hover:bg-slate-50"
                        title="Cerrar manualmente sin crear recepción (la OC sale del listado)"
                        data-testid={`btn-cerrar-${it.numero_oc}`}
                      >
                        <Lock className="w-3.5 h-3.5" /> Cerrar manual
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Paginación */}
      {stats.total > pageSize && (
        <div className="flex items-center justify-between pt-2 border-t" data-testid="pendientes-pagination">
          <span className="text-xs text-muted-foreground">
            Página <strong>{page + 1}</strong> de <strong>{totalPages}</strong> · {stats.total} OCs
          </span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} data-testid="btn-page-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages} data-testid="btn-page-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Dialog: cerrar manual */}
      <Dialog open={!!cerrarDlg} onOpenChange={(o) => { if (!o) setCerrarDlg(null); }}>
        <DialogContent className="max-w-md" data-testid="cerrar-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-700">
              <Lock className="w-5 h-5" /> Cerrar Recepción OC {cerrarDlg?.numero_oc}
            </DialogTitle>
            <DialogDescription>
              La OC dejará de aparecer en el listado de pendientes sin necesidad de crear una recepción real.
              Útil cuando la mercadería ya llegó pero no requiere registro formal en bodega.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800">
                Esta acción NO crea recepción real ni afecta stock. Solo saca la OC del listado de pendientes.
                Si necesitás registrar la entrada de material, usá <strong>"Recepcionar"</strong> en su lugar.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Motivo del cierre <span className="text-red-500">*</span></label>
              <textarea
                value={cerrarMotivo}
                onChange={(e) => setCerrarMotivo(e.target.value)}
                placeholder="Ej: Servicio externo / mercadería entregada en obra directamente / OC duplicada."
                rows={3}
                className="w-full px-3 py-2 rounded-md border border-input text-sm resize-none"
                data-testid="cerrar-motivo"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Mínimo 5 caracteres. Queda registrado en auditoría.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCerrarDlg(null)} disabled={cerrarLoading}>Cancelar</Button>
            <Button
              onClick={confirmarCerrar}
              disabled={cerrarLoading || cerrarMotivo.trim().length < 5}
              className="bg-slate-700 hover:bg-slate-800 gap-1.5"
              data-testid="cerrar-confirmar"
            >
              {cerrarLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Cerrar Recepción
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
